import { useState, useRef, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
	Plus,
	Calendar,
	X,
	FileEdit,
	Mail,
	List,
	Image,
	UserSearch,
	Circle,
	Heading
} from "lucide-react";
import useObservationQuestionTypes from "src/features/observationsAdmin/hooks/useObservationQuestionTypes/useObservationQuestionTypes";

export type QuestionTypeMenuButtonProps = {
	isDisabled?: boolean;

	onMenuItemClick: (menuItem: string, questionTypeId: number) => void;
};

const QuestionTypeMenuButton = ({
	isDisabled = false,
	onMenuItemClick
}: QuestionTypeMenuButtonProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	const { control } = useFormContext<AdminObservationForm>();

	const { data: questionTypes } = useObservationQuestionTypes();

	const hasObservationDateQuestion = useWatch({
		control,
		name: "hasObservationDateQuestion"
	});

	const hasCCEmailQuestion = useWatch({
		control,
		name: "hasCCEmailQuestion"
	});

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const getFilterIcon = (questionType: IQuestionTypeEnum) => {
		switch (questionType) {
			case "IMAGE_UPLOAD":
				return <Image size={16} />;

			case "HEADING":
				return <Heading size={16} />;

			case "CC_EMAIL_RECIPIENT":
				return <Mail size={16} />;

			case "DATE":
				return <Calendar size={16} />;

			case "OBSERVATION_DATE":
				return <Calendar size={16} />;

			case "SELECT":
				return <List size={16} />;

			case "RADIO":
				return <Circle size={16} />;

			case "TEXT":
				return <FileEdit size={16} />;

			case "EMPLOYEE_SELECTION":
				return <UserSearch size={16} />;
		}
	};

	const isQuestionTypeDisabled = (questionType: string) => {
		switch (questionType) {
			case "CC_EMAIL_RECIPIENT":
				return hasCCEmailQuestion;

			case "OBSERVATION_DATE":
				return hasObservationDateQuestion;

			default:
				return false;
		}
	};

	return (
		<div
			ref={menuRef}
			className="relative flex flex-row justify-center items-center py-4"
		>
			{/* Divider line */}
			<div className="absolute inset-x-0 top-1/2 h-px bg-border" />

			{/* Button + menu wrapper */}
			<div className="relative z-10">
				<button
					type="button"
					aria-label="Add question"
					disabled={isDisabled}
					onClick={() => !isDisabled && setIsOpen((prev) => !prev)}
					className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary text-white hover:bg-primary-dark transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isOpen ? <X size={20} /> : <Plus size={20} />}
				</button>

				{/* Dropdown menu */}
				{isOpen && (
					<div className="absolute bottom-full left-1/2 -translate-x-1/2 z-[12] mb-2 bg-white rounded-xl border border-border shadow-md flex flex-col p-1.5 min-w-[200px]">
						{[...(questionTypes ?? [])]
							.sort((a, b) => {
								const customOrder = [
									"HEADING",
									"TEXT",
									"RADIO",
									"SELECT",
									"DATE",
									"OBSERVATION_DATE",
									"CC_EMAIL_RECIPIENT",
									"IMAGE_UPLOAD",
									"EMPLOYEE_SELECTION"
								];

								const orderA = customOrder.indexOf(a.enum);
								const orderB = customOrder.indexOf(b.enum);

								return (
									(orderA === -1 ? customOrder.length : orderA) -
									(orderB === -1 ? customOrder.length : orderB)
								);
							})
							.map((questionType) => {
								const itemDisabled = isQuestionTypeDisabled(questionType.enum);
								return (
									<button
										key={questionType.id}
										type="button"
										disabled={Boolean(itemDisabled)}
										className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-foreground hover:bg-primary-lighter transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
										onClick={(e) => {
											e.stopPropagation();
											onMenuItemClick(questionType.enum, questionType.id);
											setIsOpen(false);
										}}
									>
										{getFilterIcon(questionType.enum as IQuestionTypeEnum)}
										{questionType.name}
									</button>
								);
							})}
					</div>
				)}
			</div>
		</div>
	);
};

export default QuestionTypeMenuButton;
